import React, { useEffect, useMemo, useState } from "react"
import { getAPIBaseURL, getAdmin } from "../../services/helpers"
import { getData } from "../../services/request"

const AdminAudit = () => {
  const [audits, setAudits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [actionFilter, setActionFilter] = useState("")
  const [entityTypeFilter, setEntityTypeFilter] = useState("")
  const currentAdmin = getAdmin()
  const isSuperAdmin = currentAdmin?.role === 'SUPER_ADMIN' || currentAdmin?.role === 'SUPER_MODERATOR'

  const fetchAudits = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/audits`
    const params = { limit: 200, skip: 0 }
    
    // Add filters
    if (actionFilter) {
      params.action = actionFilter
    }
    if (entityTypeFilter) {
      params.entityType = entityTypeFilter
    }
    
    // SUPER_ADMIN can filter by adminId, ADMIN cannot
    if (isSuperAdmin && query && query.match(/^[0-9a-fA-F]{24}$/)) {
      // If query looks like an admin ID, allow filtering by it
      params.adminId = query
    }

    const res = await getData(url, {}, params)
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setAudits(res.result.data?.data || [])
      } else if (res.result.status === 404) {
        setAudits([])
      } else {
        setError(res.result.data?.message || 'Failed to load audits')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchAudits()
  }, [actionFilter, entityTypeFilter, isSuperAdmin])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return audits
    
    return audits.filter(a => {
      const adminName = a.adminId?.name || ''
      const adminUsername = a.adminId?.username || ''
      const description = a.description || ''
      const action = a.action || ''
      const entityType = a.entityType || ''
      
      return (
        adminName.toLowerCase().includes(q) ||
        adminUsername.toLowerCase().includes(q) ||
        description.toLowerCase().includes(q) ||
        action.toLowerCase().includes(q) ||
        entityType.toLowerCase().includes(q)
      )
    })
  }, [audits, query])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getActionColor = (action) => {
    if (action?.includes('CREATE')) return 'bg-green-100 text-green-700'
    if (action?.includes('UPDATE') || action?.includes('CHANGE')) return 'bg-blue-100 text-blue-700'
    if (action?.includes('DELETE') || action?.includes('REMOVE')) return 'bg-red-100 text-red-700'
    if (action?.includes('ACTIVATE')) return 'bg-green-100 text-green-700'
    if (action?.includes('DEACTIVATE')) return 'bg-yellow-100 text-yellow-700'
    return 'bg-gray-100 text-gray-700'
  }

  // Get unique actions and entity types for filters
  const uniqueActions = useMemo(() => {
    const actions = new Set()
    audits.forEach(a => {
      if (a.action) actions.add(a.action)
    })
    return Array.from(actions).sort()
  }, [audits])

  const uniqueEntityTypes = useMemo(() => {
    const types = new Set()
    audits.forEach(a => {
      if (a.entityType) types.add(a.entityType)
    })
    return Array.from(types).sort()
  }, [audits])

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-600">
            {isSuperAdmin ? 'View all system activity logs' : 'View your activity logs'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex-1 min-w-[200px]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search audits (admin, description, action...)"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>
        <div className="min-w-[150px]">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[150px]">
          <select
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">All Entities</option>
            {uniqueEntityTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No audit logs found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {isSuperAdmin && <th className="px-4 py-3 font-semibold text-gray-700">Admin</th>}
                  <th className="px-4 py-3 font-semibold text-gray-700">Action</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Entity</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((audit) => (
                  <tr key={audit._id} className="hover:bg-gray-50">
                    {isSuperAdmin && (
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {audit.adminId?.name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {audit.adminId?.username || 'N/A'}
                          </div>
                          {audit.adminId?.role && (
                            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                              audit.adminId.role === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {audit.adminId.role}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getActionColor(audit.action)}`}>
                        {audit.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {audit.entityType ? (
                        <div>
                          <div className="font-medium text-gray-900">{audit.entityType}</div>
                          {audit.entityId && (
                            <div className="text-xs text-gray-500">ID: {audit.entityId}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{audit.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(audit.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminAudit
